import { Button, Spinner } from "@tellescope/design-system";
import { Checkbox, Input, Label, MultiSelect } from "@tellescope/ui";
import { HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { useViewConfigForm } from "./useViewConfigForm";

export interface ViewConfigFormProps extends HTMLAttributes<HTMLFormElement> {
  viewId?: string;
  onSubmitSuccess: (viewId: string) => void;
  onSubmitError: () => void;
  onCancel: () => void;
}

export const ViewConfigForm = ({ viewId, onSubmitSuccess, onSubmitError, onCancel, ...rest }: ViewConfigFormProps) => {
  const { t } = useTranslation("contacts");
  const {
    form,
    userOptions,
    defaultForUserOptions,
    defaultForRoleOptions,
    hiddenFromRoleOptions,
    isLoading,
    isSaving,
  } = useViewConfigForm({
    viewId,
    onSubmitSuccess,
    onSubmitError,
  });

  const titleError = form.getFieldError("title");

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Spinner size="sm" color="secondary" />
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-4" {...rest} {...form.getFormProps()}>
      <div className="space-y-2">
        <Label htmlFor="view-title">{t("Title")}</Label>
        <Input id="view-title" placeholder={t("Enter a title")} {...form.getFieldProps("title")} />
        {!!titleError.message && (
          <p id={titleError.id} className="text-sm text-destructive">
            {titleError.message}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="view-visibleToAllUsers"
          checked={form.values.visibleToAllUsers}
          onCheckedChange={(checked) => form.setFieldValue("visibleToAllUsers", checked !== false)}
        />
        <Label htmlFor="view-visibleToAllUsers">{t("Visible to all users")}</Label>
      </div>

      {!form.values.visibleToAllUsers && (
        <div className="space-y-2">
          <Label htmlFor="view-userIds">{t("Visible to specific users")}</Label>
          <MultiSelect
            id="view-userIds"
            options={userOptions}
            placeholder={t("Select users")}
            {...form.getCustomFieldProps("userIds")}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="view-defaultForUserIds">{t("Default for specific users")}</Label>
        <MultiSelect
          id="view-defaultForUserIds"
          options={defaultForUserOptions}
          placeholder={
            form.values.visibleToAllUsers || form.values.userIds.length
              ? t("Select users")
              : t("Select who can see this view first")
          }
          {...form.getCustomFieldProps("defaultForUserIds")}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="view-defaultForRoles">{t("Default for roles")}</Label>
        <MultiSelect
          id="view-defaultForRoles"
          options={defaultForRoleOptions}
          placeholder={t("Select roles")}
          {...form.getCustomFieldProps("defaultForRoles")}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="view-hiddenFromRoles">{t("Hidden from roles")}</Label>
        <MultiSelect
          id="view-hiddenFromRoles"
          options={hiddenFromRoleOptions}
          placeholder={t("Select roles")}
          {...form.getCustomFieldProps("hiddenFromRoles")}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button type="submit" disabled={!form.isValid || isSaving} size="sm">
          {t("Save")}
        </Button>
      </div>
    </form>
  );
};
